import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Code, LayoutGrid, Database, Wrench, Cloud } from "lucide-react";
import { skillCategories } from "../../data/mock";

const iconMap = {
  Code,
  LayoutGrid,
  Database,
  Wrench,
  Cloud,
};

const SkillsSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section id="skills" className="py-24 sm:py-32 relative" ref={ref} aria-label="Technical Skills and Expertise">
      {/* Subtle background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[rgba(218,255,1,0.02)] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-sm font-semibold text-[rgb(218,255,1)] uppercase tracking-widest">
            Skills & Expertise
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
            Technologies I{" "}
            <span className="text-[rgb(218,255,1)]">work with</span>
          </h2>
        </motion.div>

        {/* Skills grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, catIndex) => {
            const Icon = iconMap[category.icon];
            return (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * catIndex }}
                className="group relative p-6 rounded-2xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(218,255,1)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Category header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                    {Icon && <Icon size={20} className="text-[rgb(218,255,1)]" />}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{category.title}</h3>
                </div>

                {/* Skills tags */}
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1.5 text-sm font-medium text-[rgb(218,218,218)] bg-[rgb(38,40,42)] rounded-lg border border-[rgba(255,255,255,0.05)] hover:border-[rgba(218,255,1,0.3)] hover:text-[rgb(218,255,1)] transition-colors duration-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
